import { LikeOutlined, RollbackOutlined } from "@ant-design/icons";
import { Button, Tooltip } from "antd";
import cx from "classnames";
import useUserPermissions from "config/UserPermissionsContext/useUserPermissions";
import { startCase } from "lodash";
import React from "react";
import { useAppSelector } from "store";
import { Post } from "store/posts/postConstants";
import useUpdatePostActions from "../../utils/useUpdatePostActions";

interface ReviewStatusBannerProps {
  post: Post;
  inSelectionMode: boolean;
}

const ReviewStatusBanner: React.FC<ReviewStatusBannerProps> = ({
  post,
  inSelectionMode,
}) => {
  const { updatePostReviewStatus } = useUpdatePostActions();
  const { isAgencyOwner } = useAppSelector((state) => state.user);
  const { hasPermission } = useUserPermissions();

  const canApprovePost =
    isAgencyOwner || hasPermission("post_approval_status_approve_write", false);

  const canRejectPost =
    isAgencyOwner || hasPermission("post_approval_status_reject_write", false);

  if (post.status === "PUBLISHED" || !post.review_status || inSelectionMode) {
    return null;
  }

  const handleApproveClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click
    // TODO: Implement approve action using updatePostReviewStatus("APPROVED")
    // console.log("Approve clicked for post:", post.id);
    updatePostReviewStatus({ post, reviewStatus: "APPROVED" });
  };

  const handleRequestChangesClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click
    // TODO: Implement request changes action using updatePostReviewStatus("CHANGES_REQUESTED")
    // console.log("Request Changes clicked for post:", post.id);
    updatePostReviewStatus({ post, reviewStatus: "CHANGES_REQUESTED" });
  };

  return (
    <div
      className={cx(
        "w-full text-sm text-white font-medium px-4 py-1 rounded-t-lg flex justify-between items-center", // Adjusted: flex, justify-between, items-center, text-left (implicit)
        {
          "bg-antd-green-7": post.review_status === "APPROVED",
          "bg-antd-blue-7": post.review_status === "READY_FOR_REVIEW",
          "bg-antd-orange-7": post.review_status === "CHANGES_REQUESTED",
        }
      )}
    >
      <>
        {/* Status Text */}
        <span>
          {startCase(post.review_status?.toLowerCase()).replace("For", "for")}
        </span>

        {/* Action Buttons */}
        <div className="flex items-center space-x-1">
          {canRejectPost && (
            <Tooltip title="Request changes">
              <Button
                type="text"
                size="small"
                icon={<RollbackOutlined />}
                className="text-white hover:!bg-white/20"
                onClick={handleRequestChangesClick}
              />
            </Tooltip>
          )}
          {canApprovePost && (
            <Tooltip title="Approve">
              <Button
                type="text"
                size="small"
                icon={<LikeOutlined />}
                className="text-white hover:!bg-white/20"
                onClick={handleApproveClick}
              />
            </Tooltip>
          )}
        </div>
      </>
    </div>
  );
};

export default ReviewStatusBanner;
